/**
 * ChainVault WP - Navbar Component
 *
 * Always shows the main wallet connection.
 * Exchange page manages its own wallet independently.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';

function Navbar() {

    const {
        address,
        selectedNetwork,
        currentNetwork,
        isConnecting,
        theme,
        walletType,
        builtInWallet,
        connectMetaMask,
        connectPhantomWallet,
        disconnectWallet,
        switchNetwork,
        toggleTheme,
        formatAddress,
    } = useWallet();

    const s        = window.chainvaultSettings?.branding || {};
    const appName  = s.app_name  || 'ChainVault';
    const logoIcon = s.logo_icon || '⛓';
    const logoUrl  = s.logo_url  || '';
    const logoType = s.logo_type || 'text';
    const navLabels = {
        wallet:       s.nav_wallet       || 'Wallet',
        transactions: s.nav_transactions || 'Transactions',
        exchange:     s.nav_exchange     || 'Exchange',
        launchpad:    s.nav_launchpad    || 'Launchpad',
        predict:      s.nav_predict      || 'Predict',
        p2p:          s.nav_p2p          || 'P2P',
        apps:         s.nav_apps         || 'Apps',
        market:       s.nav_market       || 'Market',
        settings:     s.nav_settings     || 'Settings',
        earn:         s.nav_earn         || 'Earn',
    };

    const [ showNetworkDropdown, setShowNetworkDropdown ] = useState( false );
    const [ showBell,            setShowBell            ] = useState( false );
    const [ drawerOpen,          setDrawerOpen          ] = useState( false );
    const [ notifs,              setNotifs              ] = useState( [] );
    const [ unreadCount,         setUnreadCount         ] = useState( 0 );
    const bellRef = useRef( null );

    /* Load notifications from localStorage */
    React.useEffect( () => {
        const loadNotifs = () => {
            const stored = JSON.parse( localStorage.getItem('cv_notifs') || '[]' );
            setNotifs( stored );
            setUnreadCount( stored.filter( n => !n.read ).length );
        };
        loadNotifs();
        window.addEventListener( 'cv_new_notif', loadNotifs );
        return () => window.removeEventListener( 'cv_new_notif', loadNotifs );
    }, [] );

    const markAllRead = () => {
        const updated = notifs.map( n => ({...n, read:true}) );
        localStorage.setItem( 'cv_notifs', JSON.stringify(updated) );
        setNotifs( updated );
        setUnreadCount( 0 );
    };

    /* Reload notifs from storage when bell opens */
    const toggleBell = () => {
        if ( !showBell ) {
            const stored = JSON.parse( localStorage.getItem('cv_notifs') || '[]' );
            setNotifs( stored );
        }
        setShowBell( p => !p );
        setTimeout( markAllRead, 300 );
    };
    const [ showWalletDropdown,  setShowWalletDropdown  ] = useState( false );
    const [ showConnectModal,    setShowConnectModal    ] = useState( false );

    const networkRef = useRef( null );
    const walletRef  = useRef( null );
    const location   = useLocation();

    useEffect( () => {
        const handleClickOutside = ( e ) => {
            if ( networkRef.current && ! networkRef.current.contains( e.target ) ) {
                setShowNetworkDropdown( false );
            }
            if ( walletRef.current && ! walletRef.current.contains( e.target ) ) {
                setShowWalletDropdown( false );
            }
            if ( bellRef.current && ! bellRef.current.contains( e.target ) ) {
                setShowBell( false );
            }
        };
        document.addEventListener( 'mousedown', handleClickOutside );
        return () => document.removeEventListener( 'mousedown', handleClickOutside );
    }, [] );

    const isActive = ( path ) => {
        if ( path === '/' ) return location.pathname === '/';
        return location.pathname.startsWith( path );
    };

    const getWalletIcon = () => {
        if ( walletType === 'builtin'  ) return '🔑';
        if ( walletType === 'phantom'  ) return '👻';
        if ( walletType === 'metamask' ) return '🦊';
        return '💼';
    };

    const getWalletLabel = () => {
        if ( walletType === 'builtin'  ) return '🔑 ' + appName + ' Wallet';
        if ( walletType === 'phantom'  ) return '👻 Phantom';
        if ( walletType === 'metamask' ) return '🦊 MetaMask';
        return 'Wallet';
    };

    const getDisplayAddress = () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            if ( selectedNetwork === 'solana'  ) return builtInWallet.solana.address;
            if ( selectedNetwork === 'bitcoin' ) return builtInWallet.bitcoin.address;
            return builtInWallet.ethereum.address;
        }
        return address;
    };

    const displayAddress = getDisplayAddress();
    const networkOrder   = [ 'ethereum', 'solana', 'bsc', 'polygon', 'base' ];

    const handleNetworkClick = ( key ) => {
        setShowNetworkDropdown( false );
        switchNetwork( key );
    };

    const handleDisconnect = () => {
        setShowWalletDropdown( false );
        disconnectWallet();
    };

    const handleCopyAddress = () => {
        if ( displayAddress ) navigator.clipboard.writeText( displayAddress );
        setShowWalletDropdown( false );
    };

    return (
        <>
            <nav className="cv-navbar">

                { /* Mobile hamburger */ }
                <button className="cv-mobile-menu-btn" onClick={ () => setDrawerOpen( true ) } aria-label="Menu">
                    <i className="ti ti-menu-2"></i>
                </button>

                <div className="cv-navbar-logo">
                    <Link to="/" className="cv-logo-link">
                        { logoType === 'image' && logoUrl ? (
                            <img src={ logoUrl } alt={ appName } style={{ height:'32px', objectFit:'contain' }} />
                        ) : logoType === 'both' && logoUrl ? (
                            <>
                                <img src={ logoUrl } alt={ appName } style={{ height:'28px', objectFit:'contain', marginRight:'6px' }} />
                                <span className="cv-logo-text">{ appName }</span>
                            </>
                        ) : (
                            <>
                                <span className="cv-logo-icon">{ logoIcon }</span>
                                <span className="cv-logo-text">{ appName }</span>
                            </>
                        )}
                    </Link>
                </div>

                <div className="cv-navbar-links">
                    <Link to="/"             className={ `cv-nav-link ${ isActive( '/' )             ? 'active' : '' }` }>{ navLabels.wallet }</Link>
                    <Link to="/transactions" className={ `cv-nav-link ${ isActive( '/transactions' ) ? 'active' : '' }` }>{ navLabels.transactions }</Link>
                    <Link to="/exchange"     className={ `cv-nav-link ${ isActive( '/exchange' )     ? 'active' : '' }` }>{ navLabels.exchange }</Link>
                    <Link to="/launchpad"    className={ `cv-nav-link ${ isActive( '/launchpad' )    ? 'active' : '' }` }>{ navLabels.launchpad }</Link>
                    <Link to="/predict"      className={ `cv-nav-link ${ isActive( '/predict' )      ? 'active' : '' }` }>{ navLabels.predict }</Link>
                    <Link to="/p2p"          className={ `cv-nav-link ${ isActive( '/p2p' )          ? 'active' : '' }` }>{ navLabels.p2p }</Link>
                    <Link to="/apps"         className={ `cv-nav-link ${ isActive( '/apps' )         ? 'active' : '' }` }>{ navLabels.apps }</Link>
                    <Link to="/market"       className={ `cv-nav-link ${ isActive( '/market' )       ? 'active' : '' }` }>{ navLabels.market }</Link>
                    { ( window.chainvaultSettings?.earnEnabled !== false ) && <Link to="/earn"         className={ `cv-nav-link ${ isActive( '/earn' )         ? 'active' : '' }` }>{ navLabels.earn }</Link> }
                    <Link to="/settings"     className={ `cv-nav-link ${ isActive( '/settings' )     ? 'active' : '' }` }>{ navLabels.settings }</Link>
                </div>

                <div className="cv-navbar-actions">

                    { /* Network Selector */ }
                    <div className="cv-network-selector" ref={ networkRef }>
                        <button
                            className="cv-network-btn"
                            onClick={ () => setShowNetworkDropdown( prev => ! prev ) }
                        >
                            <span className="cv-network-icon">{ currentNetwork.icon }</span>
                            <span className="cv-network-name">{ currentNetwork.name }</span>
                            <span className="cv-dropdown-arrow">▾</span>
                        </button>

                        { showNetworkDropdown && (
                            <div className="cv-network-dropdown">
                                { networkOrder.map( key => {
                                    const net = {
                                        ethereum: { icon: '⟠', name: 'Ethereum'        },
                                        solana:   { icon: '◎', name: 'Solana'          },
                                        bsc:      { icon: '⬡', name: 'BNB Smart Chain' },
                                        polygon:  { icon: '⬡', name: 'Polygon'         },
                                        base:     { icon: '🔵', name: 'Base'            },
                                    }[ key ];
                                    return (
                                        <button
                                            key={ key }
                                            className={ `cv-dropdown-item ${ selectedNetwork === key ? 'active' : '' }` }
                                            onMouseDown={ ( e ) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleNetworkClick( key );
                                            } }
                                        >
                                            <span>{ net.icon }</span>
                                            <span>{ net.name }</span>
                                        </button>
                                    );
                                } ) }
                            </div>
                        ) }
                    </div>

                    { /* Main Wallet Button */ }
                    { address ? (
                        <div className="cv-wallet-connected" ref={ walletRef }>
                            <button
                                className="cv-wallet-btn connected"
                                onClick={ () => setShowWalletDropdown( prev => ! prev ) }
                            >
                                <span className="cv-wallet-dot"></span>
                                <span className="cv-wallet-icon-emoji">{ getWalletIcon() }</span>
                                { formatAddress( displayAddress ) }
                                <span className="cv-dropdown-arrow">▾</span>
                            </button>

                            { showWalletDropdown && (
                            <div className="cv-wallet-dropdown">
                                    <div className="cv-dropdown-address">{ getWalletLabel() }</div>
                                    <div className="cv-dropdown-address">{ formatAddress( displayAddress ) }</div>
                                    <button
                                        className="cv-dropdown-item"
                                        onMouseDown={ ( e ) => { e.preventDefault(); handleCopyAddress(); } }
                                    >
                                        📋 Copy Address
                                    </button>
                                    <Link
                                        to="/settings"
                                        className="cv-dropdown-item"
                                        onClick={ () => setShowWalletDropdown( false ) }
                                        style={{ display: 'block', textDecoration: 'none' }}
                                    >
                                        ⚙️ Settings
                                    </Link>

                                    <button
                                        className="cv-dropdown-item danger"
                                        onMouseDown={ ( e ) => { e.preventDefault(); handleDisconnect(); } }
                                    >
                                        🔌 Disconnect
                                    </button>
                                </div>
                            ) }
                        </div>
                    ) : (
                        <button
                            className="cv-connect-btn"
                            onClick={ () => setShowConnectModal( true ) }
                            disabled={ isConnecting }
                        >
                            { isConnecting ? 'Connecting...' : 'Connect Wallet' }
                        </button>
                    ) }

                    { /* Notification Bell */ }
                    <div ref={ bellRef } style={{ position:'relative' }}>
                        <button onClick={ toggleBell }
                            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'18px', position:'relative', padding:'4px 8px' }}
                            title="Notifications">
                            🔔
                            { unreadCount > 0 && (
                                <span style={{ position:'absolute', top:0, right:0, background:'#ef4444', color:'#fff', borderRadius:'50%', width:'16px', height:'16px', fontSize:'10px', fontWeight:'800', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    { unreadCount }
                                </span>
                            )}
                        </button>
                        { showBell && (
                            <div style={{ position:'absolute', right:0, top:'100%', width:'300px', background:'#1a1a2e', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'12px', zIndex:1000, boxShadow:'0 8px 30px rgba(0,0,0,0.3)', overflow:'hidden' }}>
                                <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                    <span style={{ fontWeight:'700', color:'#fff', fontSize:'14px' }}>🔔 Notifications</span>
                                    { notifs.length > 0 && (
                                        <button onClick={ () => {
                                            localStorage.removeItem('cv_notifs');
                                            localStorage.removeItem('cv_last_notif');
                                            setNotifs([]);
                                            setUnreadCount(0);
                                        }} style={{ background:'none', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#888', fontSize:'11px', cursor:'pointer', padding:'3px 8px', fontFamily:'inherit' }}>
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                { notifs.length === 0 ? (
                                    <div style={{ padding:'20px', textAlign:'center', color:'#555', fontSize:'13px' }}>No notifications yet</div>
                                ) : notifs.map( (n,i) => (
                                    <div key={i} onClick={ () => { if(n.url) window.open(n.url,'_blank'); setShowBell(false); } }
                                        style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.05)', cursor:'pointer', background: n.read?'transparent':'rgba(108,99,255,0.08)' }}
                                        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                                        onMouseLeave={e=>e.currentTarget.style.background=n.read?'transparent':'rgba(108,99,255,0.08)'}>
                                        <div style={{ fontWeight:'600', color:'#fff', fontSize:'13px', marginBottom:'3px' }}>{ n.title }</div>
                                        <div style={{ color:'#888', fontSize:'12px', lineHeight:'1.4' }}>{ n.message }</div>
                                        <div style={{ color:'#555', fontSize:'11px', marginTop:'4px' }}>{ new Date(n.time).toLocaleTimeString() }</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    { /* Theme Toggle */ }
                    <button className="cv-theme-toggle" onClick={ toggleTheme } title="Toggle theme">
                        { theme === 'dark' ? '☀️' : '🌙' }
                    </button>

                </div>

            </nav>

            { /* Connect Wallet Modal */ }
            { /* Mobile Drawer Overlay */ }
            { drawerOpen && (
                <div className="cv-drawer-overlay open" onClick={ () => setDrawerOpen(false) } />
            )}

            { /* Mobile Drawer */ }
            <div className={ `cv-mobile-drawer ${ drawerOpen ? 'open' : '' }` }>
                <div className="cv-drawer-header">
                    <div className="cv-logo-link">
                        <span className="cv-logo-icon">{ logoIcon }</span>
                        <span className="cv-logo-text">{ appName }</span>
                    </div>
                    <button className="cv-drawer-close" onClick={ () => setDrawerOpen(false) }>
                        <i className="ti ti-x"></i>
                    </button>
                </div>
                <div className="cv-drawer-nav">
                    <div className="cv-drawer-section">Main</div>
                    { [
                        { to: '/',             icon: 'ti-wallet',         label: navLabels.wallet },
                        { to: '/transactions', icon: 'ti-list',           label: navLabels.transactions },
                        { to: '/exchange',     icon: 'ti-arrows-exchange',label: navLabels.exchange },
                        { to: '/market',       icon: 'ti-chart-line',     label: navLabels.market },
                    ].map( ( item ) => (
                        <Link key={ item.to } to={ item.to }
                            className={ `cv-drawer-link ${ isActive(item.to) ? 'active' : '' }` }
                            onClick={ () => setDrawerOpen(false) }>
                            <i className={ `ti ${ item.icon }` }></i>
                            { item.label }
                        </Link>
                    ))}
                    <div className="cv-drawer-section">Features</div>
                    { [
                        { to: '/launchpad', icon: 'ti-rocket',        label: navLabels.launchpad },
                        { to: '/predict',   icon: 'ti-crystal-ball',  label: navLabels.predict },
                        { to: '/p2p',       icon: 'ti-users',         label: navLabels.p2p },
                        { to: '/apps',      icon: 'ti-apps',          label: navLabels.apps },
                    ].map( ( item ) => (
                        <Link key={ item.to } to={ item.to }
                            className={ `cv-drawer-link ${ isActive(item.to) ? 'active' : '' }` }
                            onClick={ () => setDrawerOpen(false) }>
                            <i className={ `ti ${ item.icon }` }></i>
                            { item.label }
                        </Link>
                    ))}
                    <div className="cv-drawer-section">Account</div>
                    <Link to="/settings" className={ `cv-drawer-link ${ isActive('/settings') ? 'active' : '' }` } onClick={ () => setDrawerOpen(false) }>
                        <i className="ti ti-settings"></i>{ navLabels.settings }
                    </Link>
                    { (window.chainvaultSettings?.branding?.referral_enabled === '1') && (
                    <Link to="/referral" className="cv-drawer-link" onClick={ () => setDrawerOpen(false) }>
                        <i className="ti ti-link"></i>Referrals
                    </Link>
                    )}
                </div>
            </div>

            { showConnectModal && (
                <div className="cv-modal-overlay" onClick={ () => setShowConnectModal( false ) }>
                    <div className="cv-modal" onClick={ e => e.stopPropagation() }>
                        <div className="cv-modal-header">
                            <h3>Connect Wallet</h3>
                            <button className="cv-modal-close" onClick={ () => setShowConnectModal( false ) }>✕</button>
                        </div>
                        <div className="cv-wallet-options">
                            <button
                                className="cv-wallet-option"
                                onClick={ () => { connectMetaMask(); setShowConnectModal( false ); } }
                            >
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg"
                                    alt="MetaMask"
                                    className="cv-wallet-option-icon"
                                />
                                <div className="cv-wallet-option-info">
                                    <strong>MetaMask</strong>
                                    <span>{ window.ethereum ? 'Installed — Ethereum & EVM' : 'Not installed' }</span>
                                </div>
                                <span className="cv-wallet-option-arrow">›</span>
                            </button>
                            <button
                                className="cv-wallet-option"
                                onClick={ () => { connectPhantomWallet(); setShowConnectModal( false ); } }
                            >
                                <img
                                    src="https://assets.coingecko.com/coins/images/4128/small/solana.png"
                                    alt="Phantom"
                                    className="cv-wallet-option-icon"
                                />
                                <div className="cv-wallet-option-info">
                                    <strong>Phantom</strong>
                                    <span>{ window?.solana?.isPhantom ? 'Installed — Solana wallet' : 'Not installed' }</span>
                                </div>
                                <span className="cv-wallet-option-arrow">›</span>
                            </button>
                            <button className="cv-wallet-option">
                                <span className="cv-wallet-option-icon">🔗</span>
                                <div className="cv-wallet-option-info">
                                    <strong>WalletConnect</strong>
                                    <span>550+ wallets — Coming soon</span>
                                </div>
                                <span className="cv-wallet-option-arrow">›</span>
                            </button>
                        </div>
                    </div>
                </div>
            ) }
        </>
    );
}

export default Navbar;